import React, { useEffect, useRef } from 'react';
import useFormScrolling from '../../hooks/useFormScrolling';
import PhoneInputField from '../../components/common/PhoneInput';
import { motion } from 'framer-motion';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useRegistration } from '../../context/RegistrationContext';
import { useModal } from '../../context/ModalContext';
import { Button } from '../../components/common/Button';
import FormField from '../../components/common/FormField';
import DateInput from '../../components/common/DateInput';
import { personalInfoSchema } from '../../utils/validationSchemas';

const PersonalInfoPage = () => {
  const { state, dispatch, actionTypes } = useRegistration();
  const { confirmAction } = useModal();
  const { formData } = state;
  const nameInputRef = useRef(null);
  
  // Aktivujeme hook pre automatické scrollovanie formulárov na mobilných zariadeniach
  useFormScrolling();
  
  // Automatický focus na pole s menom pri načítaní stránky
  useEffect(() => {
    if (nameInputRef.current) {
      nameInputRef.current.focus();
    }
  }, []);
  
  const { register, handleSubmit, control, setValue, formState: { errors } } = useForm({
    resolver: zodResolver(personalInfoSchema),
    defaultValues: formData,
  });
  
  const onSubmit = (data) => {
    dispatch({ type: actionTypes.UPDATE_FORM_DATA, payload: data });
    dispatch({ type: actionTypes.SET_STEP, payload: 3 });
  };
  
  const handleBack = () => {
    dispatch({ type: actionTypes.SET_STEP, payload: 1 });
  };

  const handleRestart = () => {
    confirmAction(
      'Začať odznova', 
      'Skutočne chcete začať registráciu odznova? Všetky doteraz zadané údaje budú stratené.', 
      () => {
        dispatch({ type: actionTypes.RESET_STATE });
        dispatch({ type: actionTypes.SET_STEP, payload: 1 });
      },
      { confirmText: 'Začať odznova', variant: 'warning' }
    );
  };

  return (
    <motion.div 
      className="container mx-auto px-4 py-8 max-w-3xl"
      initial={{ opacity: 0, x: 50 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -50 }}
      transition={{ duration: 0.3 }}
    >
      <div className="bg-white rounded-lg shadow-lg p-6 form-container">
        <h1 className="text-2xl md:text-3xl font-heading font-bold text-primary mb-6">
          Osobné údaje
        </h1>
        
        <form onSubmit={handleSubmit(onSubmit)}>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
            <FormField
              label="Meno"
              name="meno"
              register={register}
              errors={errors}
              required
              ref={nameInputRef}
            />
            
            <FormField
              label="Priezvisko"
              name="priezvisko"
              register={register}
              errors={errors}
              required
            />
            
            <div className="form-group">
              <label htmlFor="datum_narodenia" className="block text-sm font-medium mb-1">
                Dátum narodenia <span className="text-accent">*</span>
              </label>
              <Controller
                name="datum_narodenia"
                control={control}
                defaultValue={formData.datum_narodenia || ''}
                render={({ field }) => (
                  <DateInput
                    id="datum_narodenia"
                    className={`input-field ${errors.datum_narodenia ? 'border-accent' : ''}`}
                    placeholder="DD.MM.YYYY"
                    value={field.value}
                    onChange={field.onChange}
                    onBlur={field.onBlur}
                  />
                )}
              />
              {errors.datum_narodenia && (
                <p className="error-message">{errors.datum_narodenia.message}</p>
              )}
            </div>
            
            <FormField
              label="Email"
              name="email"
              type="email"
              register={register}
              errors={errors}
              required
            />
            
            <FormField
              label="Potvrďte email"
              name="email_confirm"
              type="email"
              register={register}
              errors={errors}
              required
            />
            
            <PhoneInputField
              label="Telefón"
              name="telefon"
              control={control}
              errors={errors}
              required
              setValue={setValue}
            />
            
            <FormField
              label="ICE (Kontakt v prípade núdze)"
              name="ice"
              register={register}
              errors={errors}
              placeholder="Meno a telefón"
            />
          </div>
          
          <h2 className="text-xl font-heading font-semibold text-primary mb-4">
            Adresa
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
            <FormField
              label="Ulica a číslo"
              name="ulica"
              register={register}
              errors={errors}
              required
            />
            
            <FormField
              label="Mesto"
              name="mesto"
              register={register}
              errors={errors}
              required
            />
            
            <FormField
              label="PSČ"
              name="psc"
              register={register}
              errors={errors}
              required
            />
            
            <FormField
              label="Krajina"
              name="krajina"
              register={register}
              errors={errors}
              required
            />
          </div>
          
          <h2 className="text-xl font-heading font-semibold text-primary mb-4">
            Ubytovanie a strava
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
            <FormField
              label="Typ stravy"
              name="strava"
              type="select"
              register={register}
              errors={errors}
              options={[
                { value: 'Normálna', label: 'Normálna' },
                { value: 'Vegetariánska', label: 'Vegetariánska' },
              ]}
              required
            />
            
            <FormField
              label="Typ ubytovania"
              name="ubytko_typ"
              type="select"
              register={register}
              errors={errors}
              options={[
                { value: 'Chata IPEĽ, Chatka', label: 'Chata IPEĽ, Chatka' },
                { value: 'Stan(vlastný)', label: 'Stan (vlastný)' },
              ]}
              required
            />
            
            <FormField
              label="Poznámky k ubytovaniu"
              name="ubytko_pozn"
              type="textarea"
              register={register}
              errors={errors}
              className="md:col-span-2"
            />
          </div>
          
          <h2 className="text-xl font-heading font-semibold text-primary mb-4">
            Ďalšie informácie
          </h2>
          
          <div className="grid grid-cols-1 gap-4 mb-6">
            <FormField
              label="Zdravotné obmedzenia"
              name="lek_zaznamy"
              type="textarea"
              register={register}
              errors={errors}
            />
            
            <FormField
              label="Spôsob platby"
              name="platba"
              type="select"
              register={register}
              errors={errors}
              options={[
                { value: 'Prevodom na účet', label: 'Prevodom na účet' },
                { value: 'Na mieste', label: 'Na mieste' },
              ]}
              required
            />
          </div>
          
          <div className="flex flex-col sm:flex-row justify-between gap-4">
            <div className="flex gap-2">
              <Button 
                type="button"
                variant="outline"
                onClick={handleBack}
              >
                Späť
              </Button>
              
              <Button 
                type="button"
                variant="outline"
                onClick={handleRestart}
                className="border-accent text-accent hover:bg-accent/10 hover:text-accent focus-visible:ring-accent"
              >
                Začať odznova
              </Button>
            </div>
            
            <Button 
              type="submit"
              variant="accent"
            >
              Pokračovať na výber dielne
            </Button>
          </div>
        </form>
      </div>
    </motion.div>
  );
};

export default PersonalInfoPage;
